/**
 * **El mecanismo de deshacer, y los dos ayudantes que necesita.**
 *
 * Lo llaman casi todas las mutaciones de la base de costeo, y está aparte justamente por eso:
 * metido en el archivo de materiales, el de nómina importaría de materiales algo que no tiene nada
 * que ver con un material.
 *
 * Las tres funciones eran privadas del archivo grande; al repartirlo por temas hubo que exportarlas
 * para que cada tema pueda llamarlas. **El índice `../api` no las re-exporta**: siguen siendo
 * plomería de esta carpeta, y de fuera nadie las llamaba antes.
 */

import { useMutation, type QueryClient, type QueryKey } from "@tanstack/react-query";

import { getUndoGeneration, recordChange } from "@/lib/undo";

export type Inverse = { label: string; undo: () => Promise<unknown> };

/**
 * Mutación que se puede deshacer: antes de guardar anota cómo volver atrás con lo que hay
 * en pantalla, y al guardar lo registra. Rehacer repite el mismo cambio.
 */
export function useUndoable<B, R>(run: (body: B) => Promise<R>, capture: (body: B) => Inverse | null, refresh: () => Promise<unknown>) {
  return useMutation({
    mutationFn: async (body: B) => {
      const generation = getUndoGeneration();
      const inverse = capture(body);
      const result = await run(body);
      if (inverse) recordChange({ label: inverse.label, undo: inverse.undo, redo: () => run(body) }, generation);
      return result;
    },
    onSuccess: refresh,
  });
}

export function cached<T extends { id: string }>(client: QueryClient, key: QueryKey, id: string): T | undefined {
  return client.getQueryData<T[]>(key)?.find((row) => row.id === id);
}

/** Claves que un cambio toca; comportamiento y parte variable van juntos. */
export function changedKeys(body: object): string[] {
  const keys = Object.keys(body);
  return keys.includes("cost_behavior") ? [...new Set([...keys, "variable_share"])] : keys;
}
